(ns frontend.ui.component.lp-portal.report.body.voima.fund.web
  (:require
   [common.util :as cu]
   [frontend.state.current-session :as session]
   [frontend.ui.component.grid.view :as grid]
   [frontend.ui.component.lp-portal.report.component.block-table.view :as block-table]
   [frontend.ui.component.lp-portal.report.component.horizontal-table.view :as horizontal-table]
   [frontend.ui.component.lp-portal.report.component.overview-figures.view :as ov-figures]
   [frontend.ui.component.lp-portal.report.component.vertical-table.view :as vertical-table]
   [frontend.ui.component.lp-portal.voima-components.card.style :as card-style]
   [frontend.ui.component.lp-portal.voima-components.chart-card.chart-card :as chart-card]
   [frontend.ui.component.lp-portal.voima-components.free-text-card.free-text-card :as free-text-card]
   [frontend.ui.component.lp-portal.voima-components.section.view :as section]
   [frontend.ui.component.lp-portal.voima-components.card.view :refer [card]]
   [frontend.ui.component.lp-portal.report.component.map.controller :refer [map-view]]
   [frontend.ui.component.lp-portal.voima-components.section.style :as style]
   [frontend.ui.component.lp-portal.voima-components.text-content.view :as text-content]
   [frontend.ui.component.lp-portal.report.body.voima.fund.pdf-pages.fund-overview :refer [empty-table-data?]]
   [frontend.ui.style.theme.core :as theme]
   [frontend.lp-portal.sub :as portal-sub]
   [herb.core :refer [<class]]
   [re-frame.core :as rf]))

(defn subheading
  [title]
  [:div
   {:style {:color          (theme/color [:content-card :title :color])
            :font-size      "14px"
            :font-weight    "bold"
            :padding        "12px"
            :padding-bottom "0px"
            :letter-spacing "2.2px"
            :line-height    1.5
            :text-transform :uppercase
            :width          "80%"}}
   title])


(defn overview-figures-section
  [overview-figures props]
  [section/wrapped-section "Overview"
   (str "overview-"
        (some->> props
                 :report
                 :db/id))
   [:div
    [ov-figures/overview-figures-card overview-figures props]]])

(defn executive-summary-section
  [{:keys [case-overview executive-summary-figures]} props]
  (let [block-table-config {:background-color (theme/color [:content-card :child :secondary :bg])
                            :border?          false
                            :hide-footnote?   true}]
    [section/wrapped-section "Executive summary"
     (str "executive-summary-"
          (some->> props
                   :report
                   :db/id))
     [card
      {:background-color (theme/color [:content-card :child :secondary :bg])}
      [:div
       [free-text-card/free-text-section "" case-overview props]]
      [:div
       [block-table/table "" executive-summary-figures (merge props block-table-config)]]]
     (text-content/optional-footnote executive-summary-figures props)]))


(defn fund-overview-section
  [{:keys [total-investment number-of-employees initial-and-follow-on-investments
           fund-value-bridge total-investment-by-geography
           combined-revenue-active-portfolio value-progression
           historical-fund-performance dealflow-map
           investments-map net-asset-value
           key-metrics management-fees transaction-and-other-fees
           carried-interest fund-operating-expenses
           bridge-finance-and-fund-leverage bridging-facility]
    :as   data} props]
  (let [empty-waterfall? (->> fund-value-bridge
                              :figures
                              :series
                              (first)
                              :data
                              (filter (fn [data]
                                        (not (cu/blank-value? (:y data)))))
                              (count)
                              (= 0))]
    [section/section "Fund overview"
     (str "fund-overview-"
          (some->> props
                   :report
                   :db/id))
     [:div
      [chart-card/view "Total investment (EUR)" total-investment props]
      [chart-card/view "Number of employees across active portfolio" number-of-employees props]
      [chart-card/view "Initial and follow-on investments (EUR)" initial-and-follow-on-investments props]
      [chart-card/view "Fund value bridge (EUR)" fund-value-bridge
       (merge props {:removed-since-empty? empty-waterfall?})]
      [grid/row {}
       [grid/column {:large 6}
        [chart-card/view "Total investment, by geography (EUR)" total-investment-by-geography props]
        [chart-card/view "Value progression (EUR)" value-progression props]
        [map-view dealflow-map "Deals assessed during quarter" props]]
       [grid/column {:large 6}
        [chart-card/view "Combined revenue, active portfolio (EUR)" combined-revenue-active-portfolio props]
        [chart-card/view "Historical fund performance (TVPI)" historical-fund-performance props]
        [map-view investments-map "Investment count by geography" props]]]
      (when-not (and (empty-table-data? data :net-asset-value)
                     (empty-table-data? data :key-metrics))
        [subheading "Fund performance status"])
      [horizontal-table/table "" net-asset-value props]
      [vertical-table/table "" key-metrics props]
      (when-not (and (empty-table-data? data :management-fees)
                     (empty-table-data? data :transaction-and-other-fees)
                     (empty-table-data? data :carried-interest)
                     (empty-table-data? data :fund-operating-expenses))
        [subheading
         "GP fees, carried interest and fund operating expenses"])
      [vertical-table/table "" management-fees props]
      [vertical-table/table "" transaction-and-other-fees props]
      [vertical-table/table "" carried-interest props]
      [vertical-table/table "" fund-operating-expenses props]
      (when-not (and (empty-table-data? data :bridge-finance-and-fund-leverage)
                     (empty-table-data? data :bridging-facility)
                     (not (:edit-mode? props)))
        [card {:title "Bridge finance & fund leverage facilities"}
         [grid/row {}
          [grid/column {:large 6}
           [block-table/table "" bridge-finance-and-fund-leverage props]]
          [grid/column {:large 6}
           [vertical-table/table "" bridging-facility props]]]])]]))

(defn portfolio-information-section
  [{:keys [portfolio-summary] :as portfolio-information} props]
  (let [hide-section? (and (empty-table-data? portfolio-information :portfolio-summary)
                           (not (:edit-mode? props)))]
    (when-not hide-section?
      [section/section "Portfolio information"
       (str "portfolio-information-"
            (some->> props
                     :report
                     :db/id))
       [:div
        [horizontal-table/table "Portfolio summary" portfolio-summary props]]])))

(defn investor-information-section
  [{:keys [capital-account-year-to-date capital-account-since-inception] :as investor-information} props]
  (let [hide-section? (and (empty-table-data? investor-information :capital-account-year-to-date)
                           (empty-table-data? investor-information :capital-account-since-inception)
                           (not (:edit-mode? props)))]
    (when-not hide-section?
      [section/section "Investor information"
       (str "investor-information-"
            (some->> props
                     :report
                     :db/id))
       [:div
        [horizontal-table/table "Capital account (year to date)" capital-account-year-to-date props]
        [horizontal-table/table "Capital account (since inception)" capital-account-since-inception props]]])))

(defn section-logo
  []
  [section/wrapped-section
   [:img
    {:src   (:tenant/reporting-logo-grey @(rf/subscribe [::portal-sub/tenant]))
     :class (<class style/section-wrapper-logo)}]])

(defn report-view
  [{:keys [fund-ui-data] :as props}]
  (let [{:keys [overview-figures executive-summary fund-overview portfolio-information investor-information]} fund-ui-data]
    [:div
     [:div {:class (<class style/section-wrapper)}
      [section-logo]
      [:div {:id :overview-figures}
       [:div
        [overview-figures-section overview-figures props]]]
      [:div {:id :executive-summary}
       [:div
        [executive-summary-section executive-summary props]]]]
     [:div {:id :fund-information}
      [:div
       [fund-overview-section fund-overview props]]]
     [:div {:id :portfolio-information}
      [:div
       [portfolio-information-section portfolio-information props]]]
     [:div {:id :investor-information}
      [:div
       [investor-information-section investor-information props]]]]))
